require(
  [
    'loadCss',
    'formValidator',
    'trackerRecommendedCommunities',
  ],
  (
    loadCss,
    formValidator,
  ) => {
    'use strict';

    /**
     * Returns a command object to be used to get the recommended communities.
     * @param {FormData} data - The form data containing the necessary fields.
     * @returns {Object} - The recommended command object.
     */
    function getRecommendedCommunitiesCommand(data) {
      const listingId = data.get('ListingId');
      const specId = data.get('SpecId');
      const planId = data.get('PlanId');
      const communityId = data.get('CommunityId');
      const email = data.get('MailAddress') || data.get('email');
      const marketId = data.get('MarketId');

      if (listingId) {
        return {
          ListingId: listingId,
          MailAddress: email,
          MarketId: marketId,
        };
      }

      return {
        CommunityId: communityId,
        PlanId: planId,
        SpecId: specId,
        MailAddress: email,
        MarketId: marketId,
      };
    }

    /**
     * Retrieves recommended communities based on the provided data.
     * @param {Object} data - The data used to generate the recommended communities.
     * @returns {Promise<string>} - A promise that resolves with the recommended communities.
     */
    async function getRecommendedCommunities(data) {
      const recommendedCommand = getRecommendedCommunitiesCommand(data);

      const recommended = await fetch('/ajax/leads/getrecommendedcommunities', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          leadCommand: recommendedCommand,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => response.text());

      return recommended;
    }

    /**
     * Toggles the visibility of a modal element by adding or removing the 'hidden' attribute.
     * @param {HTMLElement} modal - The modal element to toggle.
     */
    function toggleModal(modal) {
      modal.toggleAttribute('hidden');
    }

    /**
     * Shows the recommendations modal.
     * @param {string} recommended - The recommended content to display in the modal.
     * @param {HTMLElement} basicForm - The basic form element that triggered the lead.
     */
    function showRecomendationsModal(recommended, basicForm) {
      const modalExists = document.getElementById('#modal-content');

      if (!modalExists) {
        const templateElement = document.querySelector('[data-recommendations-template]');
        const template = document.createRange().createContextualFragment(templateElement.innerHTML);

        if (window.NewHomeSource.ModalLoader) {
          window.NewHomeSource.ModalLoader.createModal(template);
        }
      } else {
        const formContainer = basicForm.closest('.basic__form-wrapper');
        formContainer.setAttribute('hidden', 'hidden');
      }

      const recommendationsModal = document.querySelector('[data-basic-recommendations-modal]');

      if (recommended !== '') {
        // if the lead was requested from the on-page brochure, create the modal.
        const container = recommendationsModal.querySelector('.recommendations__container');
        const submitButton = basicForm.querySelector('[data-submit-form]');

        recommendationsModal.removeAttribute('hidden');
        container.innerHTML = recommended;
        document.body.dispatchEvent(new CustomEvent('recommededModalReady', {
          detail: {
            modal: recommendationsModal
          }
        }));

        const ga4Event = {
          eventName: 'direc_section_view',
          eventType: 'GA4Event',
          params: {
            position: submitButton.dataset.ga4IdentityPosition,
            button_name: submitButton.dataset.ga4IdentityButton_name,
          }
        };

        document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
          detail: {
            data: ga4Event,
          }
        }));
      } else {
        // emit a custom event to show the thank you modal
        document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
      }

      const modalBackground = document.querySelector('.modal_background');
      modalBackground.classList.add('modal__background-dark');

      const modalContainer = document.querySelector('#modal-content');
      modalContainer.classList.add('modal__container--recommendations');

      // reset the modal loader flag to prevent the recommendations or thank you modal from being loaded again.
      // we need to reset the flag here to allow the form to be refetched from the backend.
      if (window.NewHomeSource.ModalLoader) {
        window.NewHomeSource.ModalLoader.resetIsModalLoadedFlag();
      }
    }

    /**
     * Gets an array with the recommended communities information selected by the user.
     * @param {Object} formData with the form inputs to be submited.
     */
    function getSelectedRecommendedCommunities() {
      return Array.from(document.querySelectorAll('[data-recommendation-check]'))
        .filter((recommendation) => recommendation.checked)
        .map((recommendation) => JSON.parse(recommendation.value));
    }

    /**
     * Returns a command object to be used to get the recommended communities.
     * @param {FormData} data - The form data containing the necessary fields.
     * @returns {Object} - The recommended command object.
     */
    function getRecommendedCommunitiesSubmissionCommand(data) {
      const name = data.get('Name') || '';
      const firstName = name.split(' ')[0] || '';
      const lastName = name.split(' ')[1] || '';
      const recoComms = getSelectedRecommendedCommunities();

      return {
        CommunityId: data.get('CommunityId'),
        FromPage: data.get('FromPage'),
        Zip: data.get('ZipCode'),
        Phone: data.get('UserPhoneNumber'),
        BuilderId: data.get('BuilderId'),
        Email: data.get('MailAddress') || data.get('email') || '',
        MarketId: data.get('MarketId'),
        Type: 'postCommunityBrochureBasic',
        FirstName: firstName,
        LastName: lastName,
        RecoComms: recoComms,
        Guid: window.NewHomeSource.GuidGenerator.leadGuid,
      };
    }

    /**
     * The function dispatches a custom event named 'onRecommendationSend' with the form details as the
     * event detail.
     * @param form - The `form` parameter in the `dispatchOnRecommendationSend` function is an object
     * that represents the form data being sent when the event is dispatched.
     */
    function dispatchOnRecommendationSend(form) {
      document.body.dispatchEvent(new CustomEvent('onRecommendationSend', {
        detail: { form }
      }));
    }

    /**
     * Attaches event listeners to the recommendations modal.
     * @function
     * @returns {void}
     */
    function bindEvents(data, event) {
      const recommendationsModal = document.querySelector('[data-basic-recommendations-modal]');
      const positionProperty = event.target.dataset.ga4IdentityPosition || undefined;
      const buttonNameProperty = event.target.dataset.ga4IdentityButton_name || undefined;

      const closeButton = recommendationsModal.querySelector('[data-modal-close-element]');
      closeButton.setAttribute('data-ga4-event-position', positionProperty);
      closeButton.setAttribute('data-ga4-event-button_name', buttonNameProperty);

      const inputRecommended = recommendationsModal.querySelector('[data-direct-lead-recommendation]');
      inputRecommended.setAttribute('data-ga4-position', positionProperty);
      inputRecommended.setAttribute('data-ga4-org_button_name', buttonNameProperty);

      const skipButton = recommendationsModal.querySelector('[data-skip-button]');
      skipButton.setAttribute('data-ga4-event-position', positionProperty);
      skipButton.setAttribute('data-ga4-event-button_name', buttonNameProperty);
      skipButton.addEventListener('click', () => {
        // hide the recommendations modal
        toggleModal(recommendationsModal);

        // emit a custom event to show the thank you modal
        document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
      });

      const submitButton = recommendationsModal.querySelector('[data-recommended-button]');
      submitButton.addEventListener('click', async () => {
        const recommendationsSubmissionCommand = getRecommendedCommunitiesSubmissionCommand(data);

        // hide the recommendations modal
        toggleModal(recommendationsModal);

        // emit a custom event to show the thank you modal
        document.body.dispatchEvent(new CustomEvent('showThankYouModal'));
        const communititesChecked = getSelectedRecommendedCommunities();
        communititesChecked.forEach(dispatchOnRecommendationSend.bind(null, recommendationsModal));

        // we use a shoot and forget request to send the recommended lead data to the server
        await fetch('/ajax/leads/recommendedcommunitysubmission/', {
          method: 'POST',
          body: JSON.stringify(recommendationsSubmissionCommand),
          headers: {
            'Content-Type': 'application/json',
          },
        });
      });
    }

    async function handleBasicForm(event) {
      event.preventDefault();

      // lazy load the modals bundle.
      const bundle = document.querySelector('#basicModalsBundle').value;
      loadCss(bundle);

      const basicForm = event.target.closest('[data-form]') || document.querySelector('[data-form]');
      event.target.disabled = true;
      if (await formValidator.validateForm(basicForm)) {
        document.body.dispatchEvent(new CustomEvent('valid-lead', {
          detail: {
            form: basicForm,
            formButton: event.target,
          }
        }));

        window.NewHomeSource.GuidGenerator.leadGuid = window.NewHomeSource.GuidGenerator.generateGUID();
        const submitButton = basicForm.querySelector('[data-submit-form]');
        const data = new FormData(basicForm);
        data.append('Guid', window.NewHomeSource.GuidGenerator.leadGuid);

        // we use a shoot and forget request to send the lead data to the server
        await fetch(basicForm.action, {
          method: 'POST',
          body: data
        });

        const recommended = await getRecommendedCommunities(data);
        showRecomendationsModal(recommended, basicForm);
        bindEvents(data, event);

        // allow users to submit the form again if possible.
        submitButton.removeAttribute('disabled');

        document.body.dispatchEvent(new CustomEvent(
          'sendLeadSubmittedSegmentEvent',
          { detail: { targetEvent: event, formData: data } }
        ));
      }
      event.target.disabled = false;
    }

    function setBasicHomeFormInformation(event) {
      event.preventDefault();

      window.NewHomeSource.TrustedForm.load(() => {
        handleBasicForm(event);
      });
    }

    const submitButton = document.querySelector('[data-submit-form]');
    submitButton.addEventListener('click', setBasicHomeFormInformation);
  }
);

define("basicRecommendedModal", function(){});

